import { ExternalLinkIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Text,
  Grid,
  GridItem,
  Image,
  VisuallyHidden,
  type ButtonProps,
} from '@chakra-ui/react';
import type { GetServerSideProps } from 'next';
import isMobileDevice from 'ismobilejs';
import {
  MessageAreaSettings,
  MessageArea,
} from '../../../components/MessageArea';
import { HeroAreaSettings, HeroArea } from '../../../components/HeroArea';
import { Props as ArticleHeader } from '../../../components/ArticleSummary';
import { SPECIAL_PAGE_GA_CATEGORY } from '../../../common/constants';
import ArticlesGrid from '../../../components/ArticlesGrid';
import { sendAnalyticsEvent } from '../../../common/libs/googleAnalytics';
import { getGaCid } from '../../../server/libs/googleAnalytics';
import withAuth from '../../../server/libs/withAuth';
import createUserAccessHistory from '../../../server/libs/createUserAccessHistory';
import {
  isDoctorRecruiterAccount,
  isLicenseVerified as isLicenseVerifiedFunc,
  isPublisherAccount,
} from '../../../common/libs/user';
import { hasDemoAuthority } from '../../../common/libs/userRoleType';
import { AntaaAccountType } from '../../../common/libs/antaaAccountType';
import { getSpecialArticleProps } from '../../../server/libs/specialArticles';
import { generateS3DownloadUrl } from '../../../server/libs/siteSettings';
import { getJsonFileFromS3 } from '../../../server/libs/aws';
import articlesInSpecialPage from '../../../server/libs/articlesInSpecialPage';
import FullWidthBox from '../../../components/FullWidthBox';
import useSpecialPage from '../../../hooks/useSpecialPage';
import VideoPlayerContent from '../../../components/VideoPlayerContent';
import { getVideo, Video } from '../../../server/libs/videoArticles';

const specialPageSlug = 'summer-er-bootcamp';
const specialPagePath = `/special/${specialPageSlug}`;
const SITE_SETTING_JSON_S3_KEY = `special_articles/${specialPageSlug}/${specialPageSlug}_settings.json`;

const userAccessHistoryOperation = `special_erBootc`; // user_access_histories.operationカラムの値のため合計16文字以内

/**
 * メッセージエリアに表示するPR動画
 */
type MessageVideoSettings = {
  title: string;
  articleId: string;
  videoId: string;
  video?: Video | null; // サーバーサイドでarticleId,videoIdを元にセットされる値
};
/**
 * S3バケットに保存する専用JSONデータの型
 */
type SummerErBootcampSettings = {
  heroContent: HeroAreaSettings;
  message: MessageAreaSettings;
  messageVideo?: MessageVideoSettings;
  chapters: {
    color: string;
    iconImageS3Key: string;
    title: string;
    labels?: string[];
    note?: string;
    articleIds?: string[];
    articles?: ArticleHeader[];
  }[];
  cta: {
    mainImage: {
      pc: {
        imgS3Key: string;
      };
      mobile: {
        imgS3Key: string;
      };
    };
    // 外部リンク
    linkButtons: {
      url: string;
      label: string;
      key: string;
      variant?: ButtonProps['variant'];
      size?: ButtonProps['size'];
      width?: ButtonProps['width'];
      iconImageS3Key?: string | null;
    }[];
    speakers: {
      iconImageS3Key?: string | null;
      caption?: string[];
    }[];
  };
};

/**
 * コンポーネントに渡すpropsの型
 */
export type SummerErBootcampPageProps = {
  title: string | null;
  isMobile: boolean;
  isAuthenticated: boolean;
  isLicenseVerified: boolean;
  isLicenseVerifiedDoctor: boolean;
  isAllowAccount: boolean;
  isCorporationAccount: boolean;
  isPublisherAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  isDemoAccount: boolean;
  isPrivate: boolean;
  mainImagePath: string;
  articles?: ArticleHeader[];
  settings: SummerErBootcampSettings;
};

const getChapterId = (index: number) => `chapter-${String(index + 1)}`;

function TableOfContents({
  chapters,
  isMobile,
}: {
  chapters: SummerErBootcampSettings['chapters'];
  isMobile: boolean;
}) {
  const onClickListItem = (targetId: string) => {
    const target = document.getElementById(targetId);
    if (target) {
      window.scrollTo({
        top: target.offsetTop - 70, // ヘッダーの高さ分調整
        behavior: 'smooth',
      });
    }
  };

  return (
    <Box backgroundColor="white" py={4} w="full">
      <Box
        as="h2"
        fontSize={isMobile ? 'xl' : '2xl'}
        fontWeight="bold"
        textAlign={isMobile ? 'left' : 'center'}
      >
        確認したいシーンから探す
      </Box>
      <Box as="p" fontSize="sm" py={2} textAlign={isMobile ? 'left' : 'center'}>
        当直前や手技前など、いまの状況に近いシーンから必要なスライドを確認できます。
      </Box>
      <Grid
        py={1}
        templateColumns={
          isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))'
        }
        gap={3}
      >
        {chapters.map(({ color, iconImageS3Key, title, labels }, index) => {
          const number = index + 1;
          const zeroPaddingNumber = number < 10 ? `0${number}` : number;
          const chapterId = getChapterId(index);
          return (
            <GridItem key={chapterId} h="full">
              <Box
                as="button"
                type="button"
                py={1}
                fontSize="sm"
                display="flex"
                alignItems="center"
                onClick={() => onClickListItem(chapterId)}
                cursor="pointer"
                flexDirection="column"
                justifyContent="space-between"
                position="relative"
                bg="transparent"
                appearance="none"
                border={'1px'}
                borderColor={'gray.300'}
                borderRadius={8}
                w="full"
                h="full"
                textAlign="left"
                boxShadow="0 0 8px rgba(0,0,0,0.08)"
              >
                <Box p={isMobile ? 2 : 4}>
                  <Box
                    fontSize="xl"
                    color={color}
                    fontWeight="bold"
                    position="absolute"
                    left={isMobile ? 2 : 4}
                    top={isMobile ? 2 : 4}
                  >
                    {zeroPaddingNumber}
                  </Box>
                  <Box py={4}>
                    <Image
                      src={generateS3DownloadUrl(iconImageS3Key)}
                      alt={title}
                      height={isMobile ? 12 : 16}
                      objectFit="contain"
                      mx="auto"
                    />
                  </Box>
                  <Box textAlign="center" lineHeight={1.6} flexGrow={1}>
                    <Text fontSize="md" fontWeight="bold">
                      {title}
                    </Text>
                  </Box>
                </Box>
                <Flex
                  width="full"
                  alignItems="center"
                  justifyContent="space-between"
                  color={color}
                  minH="3em"
                  mt="auto"
                  py={1}
                  pl={2}
                >
                  <Box>
                    {labels?.map((text, index) => (
                      <Text
                        key={`${index}-${text}`}
                        fontWeight="bold"
                        fontSize="xs"
                        display="inline-block"
                      >
                        {text}
                        {index < labels.length - 1 && '・'}
                      </Text>
                    ))}
                  </Box>
                  <ChevronRightIcon w={6} h={6} />
                </Flex>
              </Box>
            </GridItem>
          );
        })}
      </Grid>
    </Box>
  );
}

function ChapterHeader({
  index,
  color,
  title,
  note,
  isMobile,
}: {
  index: number;
  color: string;
  title: string;
  note?: string;
  isMobile: boolean;
}) {
  return (
    <Flex gap={2} mb={4} flexDirection="column">
      <Heading
        id={getChapterId(index)}
        as="h3"
        fontSize={isMobile ? 'lg' : 'xl'}
        fontWeight="bold"
        p={2}
        pl={4}
        borderLeft="4px solid"
        borderColor={color}
        color={color}
      >
        {index + 1}. {title}
      </Heading>
      {note && (
        <Box fontSize="sm" color="textGray">
          {note}
        </Box>
      )}
    </Flex>
  );
}

function ChapterArticles({
  articles,
  isMobile,
}: {
  articles: ArticleHeader[];
  isMobile: boolean;
}) {
  return (
    <>
      <ArticlesGrid
        articles={articles}
        isMobile={isMobile}
        gaEventLabel={`${specialPageSlug}_articles`}
        onShowThumbnail={(articleId) => {
          sendAnalyticsEvent(
            'slide_thumbnail',
            'show',
            `special_${specialPageSlug}#${articleId}`,
          );
        }}
        onClick={(articleId) => {
          sendAnalyticsEvent(
            'slide_thumbnail',
            'click',
            `special_${specialPageSlug}#${articleId}`,
          );
        }}
      />
    </>
  );
}

function MessageVideo({ title, video }: MessageVideoSettings) {
  const gaVideoLabel = `${specialPageSlug}_video_${video?.videoId}`;

  if (!video) {
    return null;
  }

  return (
    <Box maxW="700px" mx="auto" w="full">
      <Heading
        as="h3"
        fontSize="sm"
        fontWeight="bold"
        textAlign="center"
        mb={2}
      >
        {title}
      </Heading>
      <VideoPlayerContent
        {...video}
        videoOnly
        onPlay={() =>
          sendAnalyticsEvent(SPECIAL_PAGE_GA_CATEGORY, 'play', gaVideoLabel)
        }
      />
    </Box>
  );
}

function CtaContent({
  mainImage,
  linkButtons,
  speakers,
  isMobile,
}: SummerErBootcampSettings['cta'] & {
  isMobile: boolean;
}) {
  return (
    <Flex
      gap={4}
      alignItems="center"
      py={4}
      px={isMobile ? 4 : 10}
      flexDirection="column"
      backgroundColor="white"
      borderRadius={8}
    >
      <Box maxW="700px">
        <VisuallyHidden as="h2">研修Assist</VisuallyHidden>
        <Image
          src={generateS3DownloadUrl(
            isMobile ? mainImage?.mobile?.imgS3Key : mainImage?.pc?.imgS3Key,
          )}
          alt="スライドで確認したら、次は動画で流れをつかむ"
        />
        <Flex gap={4} flexDirection="column" my={4}>
          {linkButtons?.map(
            ({ key, width, size, variant, url, label, iconImageS3Key }) => (
              <Button
                key={`${key}`}
                as="a"
                colorScheme="brand"
                w={width}
                size={size}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                display="flex"
                gap={2}
                variant={variant}
                onClick={() =>
                  sendAnalyticsEvent(
                    SPECIAL_PAGE_GA_CATEGORY,
                    'click',
                    `${specialPageSlug}_${key}`,
                  )
                }
              >
                {iconImageS3Key && (
                  <Image
                    src={generateS3DownloadUrl(iconImageS3Key)}
                    alt=""
                    aria-hidden="true"
                    boxSize={5}
                    objectFit="contain"
                  />
                )}
                {label} <ExternalLinkIcon />
              </Button>
            ),
          )}
        </Flex>
        <Box
          fontSize="xs"
          color="gray.500"
          textAlign={{
            base: 'left',
            md: 'center',
          }}
        >
          ※お試しライセンスは一定期間無料でご利用いただけます。詳細は遷移先をご確認ください。
        </Box>
      </Box>
      <Flex
        gap={6}
        flexDirection={{
          base: 'column',
          md: 'row',
        }}
        borderTop={'1px'}
        borderColor={'gray.300'}
        w="full"
        py={6}
        alignItems="center"
      >
        <Heading
          as="h3"
          fontSize={isMobile ? 'sm' : 'lg'}
          maxWidth={{
            base: undefined,
            md: '25%',
          }}
          lineHeight={1.5}
        >
          Antaa Slideでおなじみの先生の講義を、動画でも。
        </Heading>
        <Flex gap={4} flexWrap="wrap" justifyContent="center" flex={1}>
          {speakers?.map(({ iconImageS3Key, caption }, index) => (
            <Flex
              key={`${iconImageS3Key ?? 'doctor'}-${index}`}
              gap={2}
              flexDirection={{
                base: 'column',
                md: 'row',
              }}
              alignItems="center"
            >
              {iconImageS3Key && (
                <Image
                  src={generateS3DownloadUrl(iconImageS3Key)}
                  alt={caption?.[0] ?? `講師${index + 1}`}
                  boxSize={20}
                  objectFit="cover"
                  borderRadius="full"
                />
              )}
              <Box
                textAlign={{
                  base: 'center',
                  md: 'left',
                }}
                lineHeight={1.5}
              >
                {caption?.map((text, captionIndex) => (
                  <Text key={`${index}-${captionIndex}`} fontSize="xs">
                    {text}
                  </Text>
                ))}
              </Box>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
}

/**
 * スライド選集系 特集ページテンプレート
 *
 * 先生方主導で有益なスライドを特集
 */
export function SummerErBootcampPage({
  title,
  settings,
  articles,
  isMobile,
  isAuthenticated,
  isLicenseVerified,
  isCorporationAccount,
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isDemoAccount,
  isAllowAccount,
  mainImagePath,
  isPrivate,
}: SummerErBootcampPageProps) {
  const { isBlocked, blockedContent, pageHead, breadcrumbs } = useSpecialPage({
    specialPageTitle: title ?? '',
    gaLabelKey: specialPageSlug,
    specialPagePath,
    isAuthenticated,
    isLicenseVerified,
    isCorporationAccount,
    isPublisherAccount,
    isDoctorRecruiterAccount,
    isDemoAccount,
    isAllowAccount,
    mainImagePath,
    isPrivate,
    needLogin: false,
    needLicenseVerified: false,
    specialPageSlug,
  });

  if (isBlocked) {
    return blockedContent;
  }

  const { message, heroContent, chapters, cta, messageVideo } = settings;

  // 表示可能なarticlesを articleIdをキーにしたMapに変換
  const articleMap = new Map((articles ?? []).map((a) => [a.articleId, a]));
  // スライドリストを表示するchapterにarticlesを割り当てる
  const filteredChapters = chapters
    .map((chapter) => {
      const chapterArticles = (chapter.articleIds ?? [])
        .map((articleId) => articleMap.get(articleId))
        .filter((article): article is ArticleHeader => Boolean(article));

      return { ...chapter, articles: chapterArticles };
    })
    // 表示可能なarticlesが無いchapterは除外する
    .filter((chapter) => !!chapter.articles?.length);

  return (
    <>
      {pageHead}
      <Box pt={[14, 14, 14, 16]} bg="white" mb={-16}>
        {breadcrumbs}
        <HeroArea {...heroContent} isMobile={isMobile} />
        <FullWidthBox>
          <Container>
            <TableOfContents chapters={filteredChapters} isMobile={isMobile} />
          </Container>
        </FullWidthBox>
        <FullWidthBox bg="#EEF6ED">
          <Container py={4}>
            <Grid
              templateColumns={{
                base: '1fr',
                md: messageVideo?.video ? '4fr 3fr' : '1fr',
              }}
              gap={4}
              alignItems="center"
            >
              <MessageArea
                {...message}
                specialArticleId={specialPageSlug}
                isMobile={isMobile}
              />
              {messageVideo && <MessageVideo {...messageVideo} />}
            </Grid>
          </Container>
        </FullWidthBox>
        <Box px={4}>
          {filteredChapters.map((chapter, index) => {
            const chapterId = getChapterId(index);
            return (
              <Box key={chapterId} my={8}>
                <ChapterHeader {...chapter} index={index} isMobile={isMobile} />
                <ChapterArticles
                  articles={chapter?.articles ?? []}
                  isMobile={isMobile}
                />
              </Box>
            );
          })}
        </Box>
        {cta && (
          <FullWidthBox mt={16} mb={-16} bg="#EEF6ED">
            <Container pt={4} pb={16}>
              <CtaContent {...cta} isMobile={isMobile} />
            </Container>
          </FullWidthBox>
        )}
      </Box>
    </>
  );
}

export default SummerErBootcampPage;

export const getServerSideProps: GetServerSideProps<SummerErBootcampPageProps> =
  withAuth(async (context) => {
    const userId = context.req.user?.id;
    const userAgent = context.req?.headers['user-agent'] ?? '';

    void createUserAccessHistory({
      userId,
      userAgent,
      gaCid: getGaCid(context.req),
      operation: userAccessHistoryOperation,
    }).catch((e) => {
      console.error('ユーザーアクセス履歴の保存に失敗しました。', e);
    });

    const { isPrivate, canDisplay, title, topImage, isAllowAccount } =
      await getSpecialArticleProps(specialPageSlug, userId);
    if (!canDisplay) {
      return {
        notFound: true,
      };
    }

    const mainImagePath: string = generateS3DownloadUrl(topImage);
    let settings: SummerErBootcampSettings | null = null;
    try {
      settings = (await getJsonFileFromS3(
        SITE_SETTING_JSON_S3_KEY,
      )) as SummerErBootcampSettings;
    } catch (e) {
      console.error('サイト設定の取得に失敗しました。', e);
    }

    if (!settings) {
      return {
        notFound: true,
      };
    }

    let messageVideo: Video | null = null;
    if (settings?.messageVideo?.articleId && settings?.messageVideo?.videoId) {
      messageVideo = await getVideo(
        settings.messageVideo.articleId,
        settings.messageVideo.videoId,
      );
      settings.messageVideo.video = messageVideo;
    }

    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;
    const isDemoAccount = hasDemoAuthority(context.req.user?.userRoles);

    const isAuthenticated = context.req.isAuthenticated();
    const isLicenseVerified = isLicenseVerifiedFunc(context.req.user ?? null);
    const isLicenseVerifiedDoctor =
      context.req.user?.antaaAccountType === AntaaAccountType.DOCTOR &&
      isLicenseVerified;

    const articleIdList: string[] =
      settings?.chapters.flatMap(({ articleIds }) => articleIds ?? []) ?? [];

    // articleIdListの重複を排除
    const uniqueArticleIdList = [...new Set(articleIdList)];

    const articles = await articlesInSpecialPage({
      articleIdList: uniqueArticleIdList,
      isAuthenticated,
      isAllowAccount,
      user: context.req.user,
    });

    return {
      props: {
        title,
        isMobile,
        isDemoAccount,
        isAuthenticated,
        isLicenseVerified,
        isCorporationAccount:
          context.req.user?.antaaAccountType === AntaaAccountType.CORPORATION,
        isPublisherAccount: isPublisherAccount(context.req.user),
        isDoctorRecruiterAccount: isDoctorRecruiterAccount(context.req.user),
        isLicenseVerifiedDoctor,
        isAllowAccount,
        isPrivate,
        mainImagePath,
        settings,
        articles,
      },
    };
  });
